import { makeStyles } from '@material-ui/core/styles';
import Button from '@shared/components/button';
import { aspects } from '@pages/ai-product/aspects';
import { calculateBboxStyle } from '@pages/ai-product/calculateBboxStyle';
import { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import {
  createAiProduct,
  processAiProduct,
  renderAiProduct,
  updateAiProduct,
} from '@store/ai-product/actions';
import { actions } from '@store/companies';
import { selectAiProduct } from '@store/ai-product/selectors';
import { convertFileToBase64, mongoObjectId } from '@shared/utils';
import useNavigate from '@shared/router/useNavigate';
import { ROUTES } from '@app/routes/routesUrls';
import ProductImageFormDialog from '@pages/ai-product/ProductImageFormDialog';

import ProductImageEditor from '@pages/ai-product/image-editor/ProductImageEditor';
import CircularProgress from '@shared/components/circular-progress';
import Backdrop from '@shared/components/backdrop';
import DeleteIcon from '@resources/icons/DeleteIcon';
import ImageIcon from '@resources/icons/ImageIcon';
import LockIconTransparent from '@resources/icons/LockIconTransparent';
import DefineIcon from '@resources/icons/DefineIcon';
import LimitBanner from '@shared/components/page-parts/LimitBanner';

const useStyles = makeStyles((theme) => ({
  pageWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    fontSize: '14px',
    padding: '25px',
    width: '100%',
    height: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '16px',
    color: '#334D6E',
    fontWeight: 700,
    textDecoration: 'underline',
    '& > div': {
      width: '100%',
      maxWidth: '200px',
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '16px',
  },
  contentHeader: {
    display: 'inline-flex',
    flexDirection: 'column',
    gap: '6px',
    fontSize: '20px',
    fontWeight: 700,
    width: '100%',
  },
  contentSubHeader: {
    color: '#9CACC2',
    fontSize: '16px',
    fontWeight: 400,
  },
  imagesWrapper: {
    display: 'flex',
    gap: '20px',
    width: '100%',
    height: '245px',
  },
  imagesWrapperEmpty: {
    gap: 'unset',
    height: 'unset',
  },
  uploadImageList: {
    display: 'flex',
    overflowY: 'hidden',
    overflowX: 'auto',
    gap: '16px',
  },
  uploadImageWrapper: {
    width: '260px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    maxHeight: '184px',
    height: '100%',
    flex: '0 0 260px',
  },
  uploadImage: {
    width: '260px',
    flex: '0 0 184px',
    border: '1px solid #e2e2e2',
    borderRadius: '4px',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    '& > img': {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
    },
    '&.active': {
      border: `1px solid ${theme.palette.common.darkGrey}`,
    },
  },
  uploadImageModal: {
    width: '186px',
    height: '186px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    borderRadius: '4px',
    objectFit: 'cover',
    flex: '0 0 186px',
    position: 'relative',
    '& > img': {
      width: '100%',
      filter: 'opacity(0.5)',
      height: '100%',
      borderRadius: '4px',
      objectFit: 'cover',
    },
    '&.active': {
      border: `2px solid ${theme.palette.common.darkGrey}`,
      '& > img': {
        filter: 'unset',
      },
    },
  },
  productImageEditorWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    flex: '1 1 auto',
    maxWidth: '95%',
  },
  modalHeader: {
    fontSize: '20px',
    fontWeight: 700,
    width: 'calc(100% - 65px)',
    minHeight: '80px',
  },
  modalSubHeader: {
    color: '#9CACC2',
    fontSize: '16px',
    fontWeight: 400,
  },
  submitButton: {
    alignSelf: 'center',
  },
  reassignButton: {
    display: 'inline-flex',
    maxWidth: '172px',
    width: '100%',
    padding: '4px',
    alignItems: 'center',
    cursor: 'pointer',
    justifyContent: 'center',
    border: '1px solid #9CACC2',
    borderRadius: '4px',
    fontSize: '13px',
    fontWeight: 500,
  },
  reassignButtonNoObject: {
    backgroundColor: '#FEE4E3',
    color: '#B26566',
    borderColor: '#B26566',
  },
  uploadWrapper: {
    display: 'flex',
    height: '184px',
    width: '260px',
    border: '1px solid #e2e2e2',
    borderRadius: '8px',
    flexDirection: 'column',
    gap: '10px',
    alignItems: 'center',
    textAlign: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: '16px',
    flex: '0 0 260px',
    '&:last-child': {
      width: '100%',
    },
  },
  imageWrapper: {
    display: 'inline-flex',
    height: '100%',
    position: 'relative',
    '& > img': {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
    },
  },
  bbox: {
    position: 'absolute',
    border: `2px solid ${process.env.REACT_APP_PALETTE_SECONDARY_MAIN ?? '#00C98F'}`,
    borderRadius: '4px',
    boxSizing: 'border-box',
  },
  uploadImageEmpty: {
    width: '100%',
    flex: 'unset',
  },
  uploadText: {
    color: '#879AB5',
    fontSize: '13px',
  },
  uploadAccent: {
    fontSize: 'inherit',
    color: theme.palette.common,
  },
  deleteButton: {
    position: 'absolute',
    zIndex: 2,
    top: '8px',
    right: '8px',
    cursor: 'pointer',
    display: 'flex',
    border: '1px solid #E3E9F1',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff',
    borderRadius: '50%',
    padding: '4px',
    '& > svg': {
      color: '#9DA3B2',
    },
  },
  tagType: {
    padding: '2px 5px',
    display: 'inline-flex',
    alignItems: 'center',
    position: 'absolute',
    zIndex: 2,
    borderRadius: '4px',
    textTransform: 'capitalize',
    top: '8px',
    left: '8px',
    background: '#E5EFF9',
    fontSize: '12px',
    fontWeight: 400,
  },
  aspectsList: {
    display: 'flex',
    gap: '16px',
  },
  aspectsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    width: '100%',
  },
  aspectsHeader: {
    fontSize: '20px',
    fontWeight: 700,
  },
  aspectItem: {
    padding: '10px',
    border: '2px solid #e3e3e3',
    borderRadius: '8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: '1',
    justifyContent: 'center',
    textAlign: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    '&.active': {
      borderColor: theme.palette.primary.main,
    },
  },
  aspectBox: {
    width: '100%',
    border: '1px solid #000',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: 400,
    transform: 'scale(1.2)',
    maxHeight: '53px',
    maxWidth: '30px',
  },
  customInputs: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    '& > input': {
      width: '50px',
    },
  },
  changeObject: {
    display: 'flex',
    height: '200px',
    border: '1px solid #e2e2e2',
    borderRadius: '8px',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: '16px',
    width: '120px',
    textAlign: 'center',
  },
  formDialog: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  imageEditorWrapper: {
    display: 'flex',
    gap: '16px',
    flex: '1',
  },
  imageList: {
    display: 'flex',
    flex: '0 0 220px',
    flexDirection: 'column',
    padding: '0 16px',
    gap: '16px',
    overflowY: 'auto',
  },
  backdrop: {
    top: 0,
  },
  imageActions: {
    '& > button': {
      maxWidth: '13.5rem',
      height: '3rem',
    },
  },
  defineButton: {
    display: 'flex',
    flexDirection: 'row',
    gap: '10px',
    alignItems: 'center',
    justifyContent: 'center',
  },
  defineText: {
    color: '#192A3E',
    fontSize: '14px',
    fontWeight: 400,
    textTransform: 'none',
  },
  uploadWrapperReadOnly: {
    filter: 'blur(1.5px) grayscale(1)',
    pointerEvents: 'none',
  },
}));

export default function AIProduct() {
  const settings = useSelector((state) => state.companies.settings);
  const user = useSelector((state) => state.app.user);
  const classes = useStyles();
  const dispatch = useDispatch();
  const product = useSelector(selectAiProduct);
  const [groundingPrompt, setGroundingPrompt] = useState();
  const { getCurrentSettings } = actions;

  const { goTo } = useNavigate();

  const stageRef = useRef();
  const imgRefs = useRef({});
  const [isProductImageOpen, setIsProductImageOpen] = useState(false);
  const [activeImage, setActiveImage] = useState();
  const [bboxes, setBboxes] = useState({});
  useEffect(() => {
    dispatch(getCurrentSettings(user.company.id));
  }, [dispatch, user, getCurrentSettings]);

  const isAiProductReadOnly = useMemo(() => {
    return !(settings?.studioConfig?.ai_p_active ?? true);
  }, [settings]);

  useEffect(() => {
    const newBboxes = {};
    product.images.forEach((image) => {
      if (image.bboxes?.length) {
        newBboxes[image.id] = image.bboxes[0].box;
      }
    });
    setBboxes(newBboxes);
  }, [product.images]);


  const handleFileChange = async (event) => {
    const selectedImages = event.target.files;
    if (selectedImages && selectedImages.length > 0) {
      const newImages = Array.from(selectedImages);
      const promises = newImages.map(async (image) => convertFileToBase64(image));
      Promise.all(promises).then((results) => {
        const images = results.map((image) => {
          return {
            src: image,
            siloBackgroundColor: {
              r: 255,
              g: 255,
              b: 255,
            },
            siloShadowIntensity: 1,
            siloShadowGamma: 1,
            state: 'GROUNDING',
          };
        });
        dispatch(renderAiProduct({
          ...product,
          images: [...product.images, ...images],
        }));
        const action = product.id ? updateAiProduct : createAiProduct;
        dispatch(action({
          ...product,
          images: [...product.images, ...images],
        }, null, (data) => {
          const imageIds = data.images
            .filter((image) => !image.bboxes.length)
            .map((image) => image.id);
          if (imageIds.length) {
            dispatch(processAiProduct(data.id, {
              tasks: ['DETECTION'],
              images: imageIds.map((id) => ({ imageId: id })),
              isBeta: false,
              prompt: groundingPrompt,
            }));
          }
          goTo(`${ROUTES.AI_PRODUCT.ROOT}${ROUTES.AI_PRODUCT.EDIT(data.id)}`);
        }));
      });
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const droppedImages = event.dataTransfer.files;
    if (droppedImages.length > 0) {
      const newImages = Array.from(droppedImages);
      const promises = newImages.map(async (image) => convertFileToBase64(image));
      Promise.all(promises).then((results) => {
        const images = results.map((image) => {
          return {
            src: image,
            siloBackgroundColor: {
              r: 255,
              g: 255,
              b: 255,
            },
            siloShadowIntensity: 1,
            siloShadowGamma: 1,
            state: 'GROUNDING',
          };
        });
        dispatch(renderAiProduct({
          ...product,
          images: [...product.images, ...images],
        }));
        const action = product.id ? updateAiProduct : createAiProduct;
        dispatch(action({
          ...product,
          images: [...product.images, ...images],
        }, null, (data) => {
          const imageIds = data.images
            .filter((image) => !image.bboxes.length)
            .map((image) => image.id);
          if (imageIds.length) {
            dispatch(processAiProduct(data.id, {
              tasks: ['DETECTION'],
              images: imageIds.map((id) => ({ imageId: id })),
              isBeta: false,
              prompt: groundingPrompt,
            }));
          }
          goTo(`${ROUTES.AI_PRODUCT.ROOT}${ROUTES.AI_PRODUCT.EDIT(data.id)}`);
        }));
      });
    }
  };

  const handleDeleteImage = (imageId) => {
    const updatedImages = product.images.filter((image) => image.id !== imageId);
    dispatch(renderAiProduct({ ...product, images: updatedImages }));
    dispatch(updateAiProduct({ ...product, images: updatedImages }));
  };

  const handleCancel = () => {
    setIsProductImageOpen(false);
  };

  const handleSave = () => {
    const updatedProduct = {
      ...product,
      images: product.images.map((image) => {
        if (image.selectedBbox) {
          return {
            ...image,
            bboxes: image.bboxes.filter((bbox) => bbox.id === image.selectedBbox),
          };
        }
        return image;
      }),
    };
    dispatch(updateAiProduct(updatedProduct));
    setIsProductImageOpen(false);
  };

  const handleDefineObject = () => {
    const updatedImages = product.images.map((image) => {
      if (image.id === activeImage.id) {
        const imgElement = imgRefs.current[image.id];

        if (!imgElement) {
          console.error('Image element not found');
          return image;
        }

        const imgWidth = imgElement.naturalWidth || imgElement.width || 500;
        const imgHeight = imgElement.naturalHeight || imgElement.height || 500;

        const boxWidth = imgWidth * 0.2;
        const boxHeight = imgHeight * 0.2;

        const x = (imgWidth - boxWidth) / 2;
        const y = (imgHeight - boxHeight) / 2;

        return {
          ...image,
          bboxes: [
            ...image.bboxes,
            {
              box: [x, y, x + boxWidth, y + boxHeight],
              id: mongoObjectId(),
              label: 'Unknown',
              category: {
                name: 'custom',
                group: 'custom',
              },
            },
          ],
        };
      }
      return image;
    });
    dispatch(renderAiProduct({ ...product, images: updatedImages }));
  };

  useEffect(() => {
    if (activeImage) {
      setActiveImage(product.images.find((image) => image.id == activeImage.id));
    }
  }, [product.images]);

  const isStartAvailable = !!product.id;
  const isReadOnly = product?.images.some((image) => image?.alphaSrc) ?? false;
  const isHasEnvironment = product?.environment?.positionPrompt && product?.environment?.locationPrompt && product?.environment?.stylePrompt;
  const hasGroundingProcess = product?.images?.some(image => image.state === 'GROUNDING');

  const isCreateDisabled = useMemo(() => {
    return !product?.images?.length || product.images.some(image => !image.bboxes?.some(bbox => bbox?.category) || image.bboxes?.length > 1);
  }, [product?.images]);

  return (
    <div className={ classes.pageWrapper }>
      <div className={ classes.header }>
        {
          !product.images.length ? (
            'Create stunning content pack for product detail page: SILO-shots, lifestyle images, video and description.'
          ) : (
            'Start Creation'
          )
        }
      </div>
      <LimitBanner show={ isAiProductReadOnly } />
      <div className={ classes.content }>
        <div className={ classes.contentHeader }>
          Upload product photos to start generating:
          <div className={ classes.contentSubHeader }>
            Use as many different angles as possible to create the most comprehensive collection of visuals
          </div>
          {/* <TextField
            value={ groundingPrompt }
            onValueChange={ (event) => {
              setGroundingPrompt(event.target.value);
            } }
          /> */}
        </div>
        <div className={ classNames(classes.imagesWrapper, { [classes.imagesWrapperEmpty]: !product.images.length }) }>
          <label
            className={ classNames(classes.uploadWrapper, {
              [classes.uploadImageEmpty]: !product.images.length,
              [classes.uploadWrapperReadOnly]: isReadOnly,
            }) }
            htmlFor={ !isReadOnly ? 'fileUpload' : undefined }
            { ...(!isReadOnly && {
              onDrop: handleDrop,
              onDragOver: (event) => event.preventDefault(),
            }) }
          >
            <ImageIcon className={ classes.imageIcon } size="large" />
            <span className={ classes.uploadText }>Drop your image here, or <span className={ classes.uploadAccent }>browse</span></span>
            { !isReadOnly && (
              <input
                id="fileUpload"
                type="file"
                hidden
                onChange={ handleFileChange }
                accept=".png,.jpg,.jpeg,.webp"
                multiple
              />
            )}
          </label>
          <div className={ classes.uploadImageList }>
            {product.images.map((image, index) => {
              const bbox = bboxes[image.id];

              return (
                <div className={ classes.uploadImageWrapper } key={ image.id || index }>
                  <div className={ classes.uploadImage }>
                    {image.state === 'GROUNDING_FINISHED' && (image?.bboxes?.length === 1) && (
                      <div className={ classes.tagType }>
                        { image.bboxes[0].category?.name || image.bboxes[0].label.replace(/^a\s+/i, '') }
                      </div>
                    )}

                    <div className={ classes.imageWrapper }>
                      <img
                        src={ image.src }
                        alt={ `product-${index}` }
                        ref={ (el) => (imgRefs.current[image.id] = el) }
                        onLoad={ (e) => {
                          const img = e.target;
                          const bboxStyle = calculateBboxStyle(bbox, img);
                          if (bboxStyle) {
                            setBboxes((prev) => ({
                              ...prev,
                              [image.id]: bbox,
                            }));
                          }
                        } }
                      />
                      {bbox && image?.bboxes?.length <= 1 && (
                        <div
                          className={ classes.bbox }
                          style={ calculateBboxStyle(bbox, imgRefs.current[image.id]) }
                        />
                      )}
                    </div>
                    {image.state === 'GROUNDING' && (
                      <Backdrop className={ classes.backdrop }>
                        <CircularProgress size="large" />
                        Processing...
                      </Backdrop>
                    )}
                    {!isReadOnly && !hasGroundingProcess && (
                    <div
                      className={ classes.deleteButton }
                      onClick={ (e) => {
                        e.stopPropagation();
                        handleDeleteImage(image.id);
                      } }
                    >
                      <DeleteIcon />
                    </div>
                    )}
                  </div>

                  {image.state === 'GROUNDING_FINISHED' && (
                    <div
                      className={ classNames(classes.reassignButton, {
                        [classes.reassignButtonNoObject]: (image.bboxes.length > 1 || !image.bboxes.length),
                      }) }
                      onClick={ (e) => {
                        e.stopPropagation();
                        setIsProductImageOpen(true);
                        setActiveImage(image);
                      } }
                    >
                      { (image.bboxes.length > 1 || !image.bboxes.length) ? 'Choose object' : 'Edit' }
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className={ classNames(classes.aspectsWrapper, {
          [classes.uploadWrapperReadOnly]: isReadOnly,
        }) }
        >
          <span className={ classes.aspectsHeader }>
            Choose aspect ratio for future photographies:
          </span>
          <div className={ classes.aspectsList }>
            {
              aspects.map((item) => {
                return (
                  <div
                    key={ item.name }
                    className={ classNames(classes.aspectItem, { active: item.name === product.aspect?.name }) }
                    onClick={ () => {
                      dispatch(renderAiProduct({
                        ...product,
                        aspect: item,
                      }));
                    } }
                  >
                    {
                        item.name === 'Original' && (
                        <LockIconTransparent className={ classes.imageIcon } size="large" />
                        )
                    }
                    {
                        item.name === 'Custom' && (
                        <div className={ classes.customInputs }>
                          <input
                            type="number"
                            min={ 1 }
                            value={ item.x }
                            onChange={ (e) => {
                              item.x = +e.target.value;
                              dispatch(renderAiProduct({
                                ...product,
                                aspect: item,
                              }));
                            } }
                          />
                          :
                          <input
                            type="number"
                            min={ 1 }
                            value={ item.y }
                            onChange={ (e) => {
                              item.y = +e.target.value;
                              dispatch(renderAiProduct({
                                ...product,
                                aspect: item,
                              }));
                            } }
                          />
                        </div>
                        )
                    }
                    {
                        item.name !== ('Custom' && 'Original') && (
                        <div
                          className={ classes.aspectBox }
                          style={ {
                            aspectRatio: `${item.x || 1}/${item.y || 1}`,
                          } }
                        />
                        )
                    }
                    { item.name }
                  </div>
                );
              })
            }
          </div>
        </div>
        <Button
          style={ {
            alignSelf: 'center',
          } }
          disabled={ isCreateDisabled || isAiProductReadOnly }
          onClick={ () => {
            product.images.forEach((image) => {
              if (!image.alphaSrc) {
                image.state = 'REMOVING_BACKGROUND';
              }
            });
            product.category = product.images[0]?.bboxes[0]?.category;
            dispatch(updateAiProduct(product, null, () => {
              const imageIds = product.images
                .filter((image) => !image.alphaSrc)
                .map((image) => image.id);
              if (imageIds.length) {
                dispatch(processAiProduct(product.id, {
                  tasks: ['REMOVE_BACKGROUND', 'SHADOWS'],
                  images: imageIds.map((id) => ({ imageId: id })),
                  isBeta: false,
                }));
              }
              goTo(`${ROUTES.AI_PRODUCT.ROOT}${ROUTES.AI_PRODUCT.SILO(product.id)}`);
            }));
          } }
        >
          {
            !product.images.length ? (
              'Create Pack'
            ) : isHasEnvironment ? (
              'Next'
            ) : (
              'Create'
            )
          }
        </Button>
      </div>
      <ProductImageFormDialog
        Form="div"
        FormProps={ {
          className: classes.formDialog,
        } }
        open={ isProductImageOpen }
        content={ (
          <div className={ classes.imageEditorWrapper }>
            <div className={ classes.imageList }>
              { product.images.map((image, index) => {
                return (
                  <div
                    key={ index }
                    onClick={ () => {
                      setActiveImage(image);
                    } }
                    className={ classNames(classes.uploadImageModal, { active: image === activeImage }) }
                  >
                    <img
                      src={ image.src }
                      alt={ `product-${index}` }
                    />
                  </div>
                );
              })}
            </div>
            <div className={ classes.productImageEditorWrapper }>
              <div className={ classes.modalHeader }>
                {activeImage?.bboxes?.length === 1
                  ? 'Edit object'
                  : 'Choose object'}
                <div className={ classes.modalSubHeader }>
                  {activeImage?.bboxes?.length === 1
                  /* eslint-disable max-len */
                    ? 'Make sure the objects boundaries are defined correctly. Make changes if necessary.'
                    : 'Select the object on photos that you want to create a AI photo with. To do this, left-click on the product in the photo. If the required object is not found, specify its category below and start the search again.'}
                </div>
              </div>
              <div className={ classes.imageActions }>
              <Button
                color="secondary"
                variant="outlined"
                onClick={ handleDefineObject }
              >
                  <div className={ classes.defineButton }>
                    <div className={ classes.defineText }>Define object</div>
                    <DefineIcon />
                  </div>
              </Button>
              </div>
              <ProductImageEditor ref={ stageRef } image={ activeImage } mode="BBOX" />
              <Button
                className={ classes.submitButton }
                onClick={ handleSave }
              >
                Submit
              </Button>
            </div>
          </div>
        ) }
        onSave={ handleSave }
        onCancel={ handleCancel }
        onClose={ handleCancel }
        cancelButtonText="Quit"
      />
    </div>
  );
}
